import { useState, useCallback, useMemo } from 'react';
import {
  UploadIcon,
  FileTextIcon,
  WarningCircleIcon,
  CheckCircleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Button } from 'src/components/ui/button';
import {
  AlertContainer,
  AlertDescription,
  AlertTitle,
} from 'src/components/ui/alert';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';
import { Badge } from 'src/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import {
  useParsePaymentFiles,
  useGetSuppliersForMapping,
} from 'src/api/rpc/hooks/rebates/useHistoricalPaymentImport';
import type {
  ParsePaymentFilesResponse,
  SupplierMatchSuggestion,
} from '@erp/rpc';
import { cn } from 'src/lib/utils';

interface SupplierMappingEntry {
  csvSupplierName: string;
  supplierId: string;
}

interface PaymentStep1UploadProps {
  onComplete: (
    parseResult: ParsePaymentFilesResponse,
    paymentsCsvBase64: string,
    supplierMappings: SupplierMappingEntry[],
  ) => void;
  onBack: () => void;
  onError: (error: string) => void;
  isLoading: boolean;
  error?: string;
}

interface FileState {
  file: File | null;
  base64: string;
  fileName: string;
}

export default function PaymentStep1Upload({
  onComplete,
  onBack,
  onError,
  error,
}: PaymentStep1UploadProps) {
  const [paymentsFile, setPaymentsFile] = useState<FileState>({
    file: null,
    base64: '',
    fileName: '',
  });
  const [parseResult, setParseResult] =
    useState<ParsePaymentFilesResponse | null>(null);
  // Track user overrides: csvSupplierName -> selected supplierId
  const [supplierOverrides, setSupplierOverrides] = useState<
    Record<string, string>
  >({});

  const parseFilesMutation = useParsePaymentFiles();
  const { data: suppliersData } = useGetSuppliersForMapping();

  const allSuppliers = suppliersData?.suppliers ?? [];

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix (e.g., "data:text/csv;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };

  const handlePaymentsFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      try {
        const base64 = await fileToBase64(file);
        setPaymentsFile({ file, base64, fileName: file.name });
        setParseResult(null);
        setSupplierOverrides({});
      } catch (err) {
        onError('Failed to read payments file');
      }
    },
    [onError],
  );

  const handleClearPaymentsFile = () => {
    setPaymentsFile({ file: null, base64: '', fileName: '' });
    setParseResult(null);
    setSupplierOverrides({});
  };

  const handleParseFiles = async () => {
    if (!paymentsFile.base64) {
      onError('Please upload a payments file');
      return;
    }

    try {
      const result = await parseFilesMutation.mutateAsync({
        paymentsCsvBase64: paymentsFile.base64,
      });
      setParseResult(result);

      // Initialize overrides from suggestions
      const initialOverrides: Record<string, string> = {};
      for (const suggestion of result.supplierSuggestions) {
        if (suggestion.suggestedSupplierId) {
          initialOverrides[suggestion.csvSupplierName] =
            suggestion.suggestedSupplierId;
        }
      }
      setSupplierOverrides(initialOverrides);
    } catch (err) {
      // Error is handled by the mutation hook
    }
  };

  const handleSupplierChange = (
    csvSupplierName: string,
    supplierId: string,
  ) => {
    setSupplierOverrides((prev) => ({
      ...prev,
      [csvSupplierName]: supplierId,
    }));
  };

  // Build final mappings from suggestions + overrides
  const supplierMappings = useMemo((): SupplierMappingEntry[] => {
    if (!parseResult) return [];
    return parseResult.supplierSuggestions
      .filter((s) => supplierOverrides[s.csvSupplierName])
      .map((s) => ({
        csvSupplierName: s.csvSupplierName,
        supplierId: supplierOverrides[s.csvSupplierName],
      }));
  }, [parseResult, supplierOverrides]);

  // Check if all suppliers have mappings
  const allSuppliersMapped = useMemo(() => {
    if (!parseResult) return false;
    return parseResult.supplierSuggestions.every(
      (s) => supplierOverrides[s.csvSupplierName],
    );
  }, [parseResult, supplierOverrides]);

  const handleContinue = () => {
    if (!parseResult || !allSuppliersMapped) return;
    onComplete(parseResult, paymentsFile.base64, supplierMappings);
  };

  const canParse = paymentsFile.base64;
  const canContinue = parseResult && allSuppliersMapped;

  // Get confidence badge variant based on score
  const getConfidenceBadge = (score: number) => {
    if (score >= 90) {
      return (
        <Badge variant="outline" className="bg-green-500/10 text-green-600">
          {score}%
        </Badge>
      );
    }
    if (score >= 70) {
      return (
        <Badge variant="outline" className="bg-yellow-500/10 text-yellow-600">
          {score}%
        </Badge>
      );
    }
    if (score >= 50) {
      return (
        <Badge variant="outline" className="bg-orange-500/10 text-orange-600">
          {score}%
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-red-500/10 text-red-600">
        {score}%
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Upload & Map Suppliers</h2>
        <p className="text-sm text-muted-foreground">
          Upload the payments CSV and confirm supplier mappings. Headers should
          be in row 2.
        </p>
      </div>

      {error && (
        <AlertContainer variant="destructive">
          <WarningCircleIcon className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </AlertContainer>
      )}

      {/* File Upload Section */}
      <div className="max-w-md">
        <label className="text-sm font-medium">Payments CSV</label>
        {!paymentsFile.file ? (
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer bg-muted/30 hover:bg-muted/50 transition-colors mt-2">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              <UploadIcon className="w-8 h-8 mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Click to upload payments CSV
              </p>
            </div>
            <input
              type="file"
              className="hidden"
              accept=".csv"
              onChange={handlePaymentsFileChange}
            />
          </label>
        ) : (
          <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/30 mt-2">
            <div className="flex items-center gap-2">
              <FileTextIcon className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium truncate max-w-[300px]">
                {paymentsFile.fileName}
              </span>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClearPaymentsFile}>
              <XIcon className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {/* Parse Button */}
      {canParse && !parseResult && (
        <div className="flex justify-start">
          <Button
            onClick={handleParseFiles}
            disabled={parseFilesMutation.isPending}
          >
            {parseFilesMutation.isPending ? 'Parsing...' : 'Parse File'}
          </Button>
        </div>
      )}

      {/* Parse Results */}
      {parseResult && (
        <div className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold">{parseResult.totalPayments}</p>
              <p className="text-sm text-muted-foreground">Payments</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold">
                {parseResult.uniqueSuppliers}
              </p>
              <p className="text-sm text-muted-foreground">Suppliers</p>
            </div>
            <div className="p-4 bg-muted/30 rounded-lg text-center">
              <p className="text-2xl font-bold">
                ${parseResult.totalAmount.toLocaleString()}
              </p>
              <p className="text-sm text-muted-foreground">Total Amount</p>
            </div>
          </div>

          {/* Mapping Status */}
          {allSuppliersMapped ? (
            <AlertContainer className="border-green-500/50 bg-green-500/10">
              <CheckCircleIcon className="h-4 w-4 text-green-500" />
              <AlertTitle>All Suppliers Mapped</AlertTitle>
              <AlertDescription>
                All {parseResult.uniqueSuppliers} suppliers have been mapped.
                Review and adjust if needed, then continue.
              </AlertDescription>
            </AlertContainer>
          ) : (
            <AlertContainer variant="destructive">
              <WarningCircleIcon className="h-4 w-4" />
              <AlertTitle>Missing Supplier Mappings</AlertTitle>
              <AlertDescription>
                Some suppliers need to be mapped before you can continue. Use
                the dropdown to select the correct supplier.
              </AlertDescription>
            </AlertContainer>
          )}

          {/* Supplier Mapping Table */}
          <div>
            <h3 className="text-sm font-medium mb-2">Supplier Mapping</h3>
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>CSV Supplier Name</TableHead>
                    <TableHead>Best Match</TableHead>
                    <TableHead>Score</TableHead>
                    <TableHead>Mapped To</TableHead>
                    <TableHead className="text-right">Payments</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parseResult.supplierSuggestions.map((suggestion, idx) => {
                    const selectedSupplierId =
                      supplierOverrides[suggestion.csvSupplierName];
                    const isMapped = Boolean(selectedSupplierId);

                    return (
                      <TableRow
                        key={idx}
                        className={cn(!isMapped && 'bg-red-500/5')}
                      >
                        <TableCell className="font-medium">
                          {suggestion.csvSupplierName}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {suggestion.suggestedSupplierName || (
                            <span className="text-red-500">No match</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {suggestion.suggestedSupplierId
                            ? getConfidenceBadge(suggestion.similarityScore)
                            : '-'}
                        </TableCell>
                        <TableCell className="min-w-[250px]">
                          <Select
                            value={selectedSupplierId || ''}
                            onValueChange={(value) =>
                              handleSupplierChange(
                                suggestion.csvSupplierName,
                                value,
                              )
                            }
                          >
                            <SelectTrigger
                              className={cn(
                                'w-full',
                                !isMapped && 'border-red-500',
                              )}
                            >
                              <SelectValue placeholder="Select supplier..." />
                            </SelectTrigger>
                            <SelectContent>
                              {allSuppliers.map((supplier) => (
                                <SelectItem
                                  key={supplier.id}
                                  value={supplier.id}
                                >
                                  {supplier.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-right">
                          {suggestion.paymentCount}
                        </TableCell>
                        <TableCell className="text-right">
                          ${suggestion.totalPaymentAmount.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-between pt-4 border-t">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button
          onClick={handleContinue}
          disabled={!canContinue}
          className={cn(!canContinue && 'opacity-50')}
        >
          Continue to Import
        </Button>
      </div>
    </div>
  );
}
